import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import api, { formatRupiah } from "@/lib/api";
import LikeButton from "@/components/LikeButton";
import { ArrowRight, Zap, Shield, Layers, Truck, Star, TrendingUp, Users, ShoppingBag } from "lucide-react";

// Data fiktif untuk live feed
const generateFeed = () => {
  const names = ["A***", "B***", "R***", "D***", "F***", "K***", "M***", "S***", "J***", "T***"];
  const types = ["WITHDRAW", "DEPOSIT"];
  const type = types[Math.floor(Math.random() * types.length)];
  const amount = Math.floor(Math.random() * 498 + 2) ;
  const name = names[Math.floor(Math.random() * names.length)];
  return { type, amount, name, id: Math.random() };
};

export default function Home() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [feed, setFeed] = useState(() => Array.from({ length: 6 }, generateFeed));
  const [heroIndex, setHeroIndex] = useState(0);
  const [stats, setStats] = useState({ users: 0, orders: 0, products: 0, volume: 0 });
  const statsRef = useRef(null);
  const [statsStarted, setStatsStarted] = useState(false);

  const heroSlides = [
    {
      tag: "// NO CAP, STRAIGHT FACTS",
      title: <>No drama.<br /><span className="bg-black text-[#FF6A00] px-2">Just vibe</span>.<br />Cash in.</>,
      sub: "Platform jual beli untuk penjual lokal dengan pasar yang lebih luas.",
    },
    {
      tag: "// GET PAID EVERY REFERRAL",
      title: <>Review product<br /><span className="bg-black text-[#FF6A00] px-2">now and get</span><br />commission.</>,
      sub: "Undang teman, review produk, dan dapatkan komisi 10% otomatis setiap transaksi.",
    },
    {
      tag: "// LOW FEE HIGH REWARD",
      title: <>Komisi cuma<br /><span className="bg-black text-[#FF6A00] px-2">5%</span>.<br />Iklan 0%.</>,
      sub: "Biaya iklan 0%, komisi platform hanya 5% per transaksi. Sistem referral built-in.",
    },
  ];

  // Auto slide hero
  useEffect(() => {
    const t = setInterval(() => setHeroIndex((i) => (i + 1) % heroSlides.length), 4000);
    return () => clearInterval(t);
  }, []);

  // Live feed
  useEffect(() => {
    const t = setInterval(() => {
      setFeed((prev) => [generateFeed(), ...prev.slice(0, 7)]);
    }, 2000);
    return () => clearInterval(t);
  }, []);

  // Animated stats
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !statsStarted) {
        setStatsStarted(true);
        const targets = { users: 12400, orders: 89300, products: 340, volume: 2800000 };
        const duration = 2000;
        const steps = 60;
        let step = 0;
        const timer = setInterval(() => {
          step++;
          const progress = step / steps;
          setStats({
            users: Math.floor(targets.users * progress),
            orders: Math.floor(targets.orders * progress),
            products: Math.floor(targets.products * progress),
            volume: Math.floor(targets.volume * progress),
          });
          if (step >= steps) clearInterval(timer);
        }, duration / steps);
      }
    }, { threshold: 0.3 });
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, [statsStarted]);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data.slice(0, 8))).catch(() => {});
    api.get("/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">

      {/* HERO CAROUSEL */}
      <section className="relative border-b border-black overflow-hidden bg-[#FF6A00] text-white min-h-[85vh] flex items-center">
        <div className="absolute inset-0 grid-line opacity-20" />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-24 grid lg:grid-cols-12 gap-8 items-center w-full">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 border border-black px-3 py-1 mb-6 bg-black text-[#FFD600]">
              <span className="w-1.5 h-1.5 bg-[#FFD600] rounded-full animate-blink" />
              <span className="label-mono">{heroSlides[heroIndex].tag}</span>
            </div>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-black uppercase tracking-tighter leading-[0.9] text-white transition-all duration-500">
              {heroSlides[heroIndex].title}
            </h1>
            <p className="text-base md:text-lg text-white/95 mt-6 max-w-xl leading-relaxed">
              {heroSlides[heroIndex].sub}
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <Link to="/catalog" className="bg-black text-white px-6 py-3 font-mono uppercase text-xs font-bold tracking-widest border border-black hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2">
                Belanja sekarang <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/register" className="bg-white text-black px-6 py-3 font-mono uppercase text-xs font-bold tracking-widest border border-black shadow-brutal hover:translate-x-[3px] hover:translate-y-[3px] hover:shadow-none transition-all">
                Daftar gratis
              </Link>
            </div>
            {/* Carousel dots */}
            <div className="flex gap-2 mt-8">
              {heroSlides.map((_, i) => (
                <button key={i} onClick={() => setHeroIndex(i)}
                  className={`w-8 h-1.5 transition-all ${i === heroIndex ? "bg-white" : "bg-white/40"}`}
                />
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 grid grid-cols-2 gap-0 border border-black">
            <div className="aspect-square overflow-hidden border-r border-b border-black">
              <img src="https://images.unsplash.com/photo-1695073621086-aa692bc32a3d?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="" className="w-full h-full object-cover" />
            </div>
            <div className="aspect-square overflow-hidden border-b border-black bg-[#FFD600] p-6 flex flex-col justify-end">
              <div className="label-mono text-black">// LIVE</div>
              <div className="font-display text-3xl font-black uppercase mt-1 leading-tight text-black">Top up<br />instan</div>
            </div>
            <div className="aspect-square overflow-hidden border-r border-black bg-black text-white p-6 flex flex-col justify-end">
              <div className="label-mono text-[#FF6A00]">// GET PAID</div>
              <div className="font-display text-2xl font-black uppercase mt-1 leading-tight">Review &<br />Earn 💰</div>
            </div>
            <div className="aspect-square overflow-hidden">
              <img src="https://images.unsplash.com/photo-1768166206522-dfffc0783b0c?crop=entropy&cs=srgb&fm=jpg&q=85&w=600" alt="" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <div className="border-b border-black bg-black text-[#FF6A00] overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-3">
          {Array.from({ length: 3 }).map((_, k) => (
            <div key={k} className="flex items-center gap-12 px-6 font-mono text-sm uppercase font-bold tracking-widest">
              <span>// FULFILLMENT 99.97%</span><span>★</span>
              <span>// REVIEW & GET COMMISSION 🔥</span><span>★</span>
              <span>// AUTO DISPATCH 24/7</span><span>★</span>
              <span>// KOMISI CUMA 5%</span><span>★</span>
              <span>// IKLAN 0%</span><span>★</span>
              <span>// REFERRAL 10% OTOMATIS</span><span>★</span>
              <span>// 12K+ MEMBERS</span><span>★</span>
            </div>
          ))}
        </div>
      </div>

      {/* LIVE FEED — Withdraw/Deposit */}
      <section className="border-b border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
          <div className="flex items-center gap-3 mb-4">
            <span className="w-2 h-2 bg-[#00C853] rounded-full animate-blink" />
            <span className="label-mono text-[#00C853]">// LIVE TRANSACTION FEED</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
            {feed.map((item) => (
              <div key={item.id} className={`border p-2 text-xs font-mono ${item.type === "WITHDRAW" ? "border-[#FF3333] text-[#FF3333]" : "border-[#00C853] text-[#00C853]"}`}>
                <div className="font-bold">{item.type}</div>
                <div className="text-white">{item.name}</div>
                <div className="font-bold">${parseFloat(item.amount).toFixed(2)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ANIMATED STATS */}
      <section ref={statsRef} className="border-b border-black bg-[#FF6A00]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0 border-l border-t border-black">
            {[
              { icon: Users, label: "Members", value: stats.users.toLocaleString() + "+" },
              { icon: ShoppingBag, label: "Orders", value: stats.orders.toLocaleString() + "+" },
              { icon: Layers, label: "Produk", value: stats.products.toLocaleString() + "+" },
              { icon: TrendingUp, label: "Volume", value: "$" + (stats.volume / 1000000).toFixed(1) + "M+" },
            ].map((s, i) => (
              <div key={i} className="border-r border-b border-black p-6 bg-white text-black text-center">
                <s.icon className="w-6 h-6 text-[#FF6A00] mx-auto mb-2" />
                <div className="font-display text-3xl font-black">{s.value}</div>
                <div className="label-mono text-zinc-500 mt-1">// {s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CATEGORIES */}
      <section className="border-b border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex justify-between items-end mb-8">
            <div>
              <div className="label-mono text-zinc-500">// 01</div>
              <h2 className="font-display text-4xl font-black uppercase tracking-tight mt-1">Kategori</h2>
            </div>
            <Link to="/catalog" className="label-mono hover:text-[#FF6A00]">Lihat semua →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 border-l border-t border-black">
            {categories.map((c) => (
              <Link key={c.id} to={`/catalog?category=${c.id}`} className="border-r border-b border-black p-6 hover:bg-[#FF6A00] hover:text-white group transition-colors">
                <div className="label-mono text-zinc-500 group-hover:text-white">// {c.slug}</div>
                <div className="font-display text-xl font-black uppercase mt-2">{c.name}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="border-b border-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <div className="flex justify-between items-end mb-8">
            <div>
              <div className="label-mono text-zinc-500">// 02</div>
              <h2 className="font-display text-4xl font-black uppercase tracking-tight mt-1">Produk Pilihan</h2>
            </div>
            <Link to="/catalog" className="label-mono hover:text-[#FF6A00]">Semua produk →</Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 border-l border-t border-black">
            {products.map((p) => (
              <Link key={p.id} to={`/product/${p.id}`} className="group border-r border-b border-black bg-white relative">
                <div className="aspect-square overflow-hidden border-b border-black bg-zinc-100 relative">
                  <img src={p.image_url} alt={p.name} className="w-full h-full object-cover group-hover:grayscale transition-all duration-300" />
                  <div className="absolute top-2 right-2"><LikeButton product={p} /></div>
                  {p.sold_count > 0 && (
                    <div className="absolute bottom-2 left-2 badge-status bg-black text-white">🔥 {p.sold_count} TERJUAL</div>
                  )}
                </div>
                <div className="p-4">
                  <span className="badge-status bg-white">{p.type === "digital" ? "DIGITAL" : "FISIK"}</span>
                  <h3 className="font-display text-base font-bold uppercase mt-2 line-clamp-1">{p.name}</h3>
                  <div className="flex items-center justify-between mt-3">
                    <span className="font-mono text-sm font-bold">{formatRupiah(p.price)}</span>
                    <span className="label-mono text-[#FF6A00]">BUY →</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEW & EARN BANNER */}
      <section className="border-b border-black bg-black text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">
          <div className="label-mono text-[#FFD600] mb-4">// GET REWARDED</div>
          <h2 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            Review Product Now<br />
            <span className="text-[#FF6A00]">& Get Commission</span> 💸
          </h2>
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            Tulis review jujur, bantu pembeli lain, dan dapatkan komisi otomatis setiap kali produkmu dibeli lewat referralmu.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-8">
            <Link to="/register" className="bg-[#FF6A00] text-white px-8 py-4 font-mono uppercase text-sm font-bold tracking-widest border border-[#FF6A00] hover:bg-white hover:text-black transition-colors inline-flex items-center gap-2">
              Mulai sekarang <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-black bg-[#FF6A00] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16">
          <div className="label-mono text-white/80">// 03</div>
          <h2 className="font-display text-4xl font-black uppercase tracking-tight mt-1 mb-10">Kenapa AleShopNow?</h2>
          <div className="grid md:grid-cols-4 gap-0 border-l border-t border-black">
            {[
              { icon: Zap, title: "Instan", desc: "Order produk digital diproses otomatis dalam hitungan detik. No waiting." },
              { icon: Shield, title: "Aman", desc: "Setiap transaksi tercatat dan terlindungi. Saldo aman 100%." },
              { icon: Layers, title: "Multi-produk", desc: "Top up game, voucher, pulsa, sneakers, gadget — satu platform semua ada." },
              { icon: Star, title: "Earn", desc: "Review produk & undang teman. Komisi 10% masuk otomatis ke saldomu." },
            ].map((f, i) => (
              <div key={i} className="border-r border-b border-black p-6 bg-white text-black">
                <f.icon className="w-6 h-6 text-[#FF6A00]" />
                <div className="font-display text-xl font-black uppercase mt-4">{f.title}</div>
                <p className="text-sm text-zinc-600 mt-2 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}
